import React from 'react';
import { useFormContext } from 'react-hook-form';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import cuid from 'cuid';
import snakeCase from 'lodash/snakeCase';
import ConstituentIcons from '@/lib/configs/ConstituentIcons';
import translate from '@/lib/intl/translate';
import { ConstituentType } from '@/lib/types/ConstituentPropType';
import ConstituentField from '../constants/ConstituentField';

const IGNORE_FIELDS = [
  ConstituentField.IS_DONOR_RULESET,
  ConstituentField.IS_MAJOR_DONOR_RULESET,
  ConstituentField.IS_RECURRING_DONOR_RULESET,
];

const ORG_ONLY_FIELDS = [
  ConstituentField.CATEGORY_CORPORATION,
  ConstituentField.CATEGORY_FOUNDATION,
  ConstituentField.CATEGORY_GOVERNMENT,
];

function CategorySelect() {
  const [uid] = React.useState(cuid());
  const { setValue, watch } = useFormContext();
  const constituentType = watch(ConstituentField.CONSTITUENT_TYPE);

  const options = React.useMemo(
    () =>
      ConstituentIcons.reduce((arr, { icon, label, value }) => {
        const field = ConstituentField[snakeCase(value).toUpperCase()];
        if (
          !field ||
          IGNORE_FIELDS.includes(field) ||
          (!(constituentType === ConstituentType.ORG) &&
            ORG_ONLY_FIELDS.includes(field))
        ) {
          return arr;
        }
        return [...arr, { icon, field, label }];
      }, []),
    [constituentType],
  );
  const watchedValues = watch(options.map(({ field }) => field));

  const internalValue = React.useMemo(
    () => options.filter((_, i) => watchedValues[i]),
    [watchedValues],
  );

  const handleChange = (event) => {
    const selectedFields = event.target.value.map(({ field }) => field);
    options.forEach(({ field }) =>
      setValue(field, selectedFields.includes(field)),
    );
  };

  return (
    <FormControl className="col-span-full" size="small">
      <InputLabel id={`${uid}-label`}>{translate('CATEGORY')}</InputLabel>
      <Select
        labelId={`${uid}-label`}
        id={uid}
        label={translate('CATEGORY')}
        multiple
        value={internalValue}
        onChange={handleChange}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        }}
        renderValue={(selected) => (
          <div className="max-w-full truncate">
            {selected.map((item, i) => (
              <div key={item.field} className="mr-3 inline-flex items-center">
                {item.icon && <span className="mr-1">{item.icon}</span>}
                {item.label + (i < selected.length - 1 ? ',' : '')}
              </div>
            ))}
          </div>
        )}
      >
        {options.map((item) => (
          <MenuItem key={item.field} value={item}>
            <div className="flex w-full items-center">
              {item.icon}
              <div className="mx-3">{item.label}</div>
              <Checkbox
                className="ml-auto"
                checked={internalValue.includes(item)}
              />
            </div>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default CategorySelect;
